// src/app/guards/auth.guards.ts
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Auth, user } from '@angular/fire/auth';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { map, take, switchMap } from 'rxjs/operators';
import type { CanActivateFn } from '@angular/router';

// Helper: load user document
const loadUserDoc = async (uid: string, firestore: Firestore) => {
  const ref = doc(firestore, `users/${uid}`);
  const snap = await getDoc(ref);
  return snap.data() as any;
};
export const applicationsGuard: CanActivateFn = () => {
  const auth = inject(Auth);
  const router = inject(Router);
  const firestore = inject(Firestore);

  return user(auth).pipe(
    take(1),
    switchMap(async u => {
      if (!u) return router.parseUrl('/login');

      const data = await loadUserDoc(u.uid, firestore);

      return data?.role === 'admin' || data?.role === 'manager'
        ? true
        : router.parseUrl('/tenant');
    })
  );
};
export const adminHomeGuard: CanActivateFn = () => {
  const auth = inject(Auth);
  const router = inject(Router);
  const firestore = inject(Firestore);

  return user(auth).pipe(
    take(1),
    switchMap(async u => {
      if (!u) return router.parseUrl('/login');

      const data = await loadUserDoc(u.uid, firestore);

      if (data?.role === 'manager') {
        return router.parseUrl('/admin/applications');
      }

      if (data?.role === 'admin') {
        return router.parseUrl('/admin/dashboard');
      }

      return router.parseUrl('/tenant');
    })
  );
};
// ADMIN GUARD
export const adminGuard: CanActivateFn = () => {
  const auth = inject(Auth);
  const router = inject(Router);
  const firestore = inject(Firestore);

  return user(auth).pipe(
    take(1),
    switchMap(async u => {
      if (!u) return router.parseUrl('/login');

      const data = await loadUserDoc(u.uid, firestore);
      return data?.role === 'admin'
        ? true
        : router.parseUrl('/tenant');
    })
  );
};

// TENANT GUARD
export const tenantGuard: CanActivateFn = () => {
  const auth = inject(Auth);
  const router = inject(Router);
  const firestore = inject(Firestore);

  return user(auth).pipe(
    take(1),
    switchMap(async u => {
      if (!u) return router.parseUrl('/login');

      const data = await loadUserDoc(u.uid, firestore);
      return data?.role === 'tenant'
        ? true
        : router.parseUrl('/admin');
    })
  );
};

// LOGIN GUARD (Block login if already authenticated)
export const loginGuard: CanActivateFn = () => {
  const auth = inject(Auth);
  const router = inject(Router);
  const firestore = inject(Firestore);

  return user(auth).pipe(
    take(1),
    switchMap(async u => {
      if (!u) return true;

      const data = await loadUserDoc(u.uid, firestore);
      if (data?.role === 'admin') {
        return router.parseUrl('/admin');
      }

      if (data?.role === 'manager') {
        return router.parseUrl('/admin/applications');
      }

      return router.parseUrl('/tenant');
    })
  );
};
